import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Edit, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { DetailPageHeader } from '@/components/common/DetailPageHeader';
import { DetailCard } from '@/components/common/DetailCard';
import { DetailField } from '@/components/common/DetailField';
import { StatusBadge } from '@/components/common/StatusBadge';
import { InsuranceCompanyForm } from '../components/InsuranceCompanyForm';
import { AdjustorList } from '../components/AdjustorList';
import { AdjustorForm } from '../components/AdjustorForm';
import { useInsuranceCompany } from '../hooks/useInsuranceCompany';
import { useAdjustors } from '../hooks/useAdjustors';
import { deleteAdjustor } from '../api/insurance';
import type { InsuranceAdjustor } from '../types';

export function InsuranceCompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = id ? parseInt(id, 10) : undefined;

  const [isCompanyFormOpen, setIsCompanyFormOpen] = useState(false);
  const [isAdjustorFormOpen, setIsAdjustorFormOpen] = useState(false);
  const [selectedAdjustor, setSelectedAdjustor] = useState<InsuranceAdjustor | null>(null);
  const [showInactiveAdjustors, setShowInactiveAdjustors] = useState(false);

  const { data: company, isLoading: isLoadingCompany, error: companyError, refetch: refetchCompany } =
    useInsuranceCompany(companyId);

  const { data: adjustors, isLoading: isLoadingAdjustors, refetch: refetchAdjustors } =
    useAdjustors(companyId, showInactiveAdjustors ? undefined : { isActive: true });

  const handleEditCompany = () => {
    setIsCompanyFormOpen(true);
  };

  const handleAddAdjustor = () => {
    setSelectedAdjustor(null);
    setIsAdjustorFormOpen(true);
  };

  const handleEditAdjustor = (adjustor: InsuranceAdjustor) => {
    setSelectedAdjustor(adjustor);
    setIsAdjustorFormOpen(true);
  };

  const handleDeactivateAdjustor = async (adjustor: InsuranceAdjustor) => {
    if (!window.confirm(`Are you sure you want to deactivate "${adjustor.fullName}"?`)) {
      return;
    }

    try {
      await deleteAdjustor(adjustor.id);
      refetchAdjustors();
      refetchCompany(); // Update adjustor count
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to deactivate adjustor');
    }
  };

  const handleCompanyFormSuccess = () => {
    refetchCompany();
  };

  const handleAdjustorFormSuccess = () => {
    refetchAdjustors();
    refetchCompany(); // Update adjustor count
  };

  if (isLoadingCompany) {
    return (
      <div className="flex h-[calc(100vh-200px)] items-center justify-center">
        <LoadingSpinner size="lg" label="Loading company..." />
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="container mx-auto p-6 max-w-[1400px]">
        <div className="bg-white rounded-[20px] border border-[#d9d9d9] p-4">
          <Button variant="ghost" onClick={() => navigate('/insurance')} className="mb-4">
            Back to Companies
          </Button>
          <ErrorAlert error={companyError || 'Company not found'} />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-[1400px]">
      {/* Header */}
      <DetailPageHeader
        backTo={{ path: '/insurance', label: 'Back to Companies' }}
        left={
          <>
            <h1 className="text-2xl font-bold text-[#0A0A9E]">{company.name}</h1>
            <StatusBadge variant={company.isActive ? 'active' : 'inactive'}>
              {company.isActive ? 'Active' : 'Inactive'}
            </StatusBadge>
          </>
        }
        right={
          <Button onClick={handleEditCompany} variant="outline" size="sm" className="rounded-[50px]">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        }
      />

      {/* Content Area */}
      <div className="bg-[#f6f6f6] rounded-lg min-h-[400px]">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
          {/* Company Information - Left Column */}
          <DetailCard title="Company Information" className="lg:col-span-1">
            <div className="space-y-4">
              <DetailField label="Address">
                {(company.address1 || company.address2 || company.city || company.state || company.zip) && (
                  <>
                    {company.address1 && <div>{company.address1}</div>}
                    {company.address2 && <div>{company.address2}</div>}
                    {(company.city || company.state || company.zip) && (
                      <div>
                        {company.city}
                        {company.city && company.state && ', '}
                        {company.state} {company.zip}
                      </div>
                    )}
                  </>
                )}
              </DetailField>

              <DetailField label="Phone" value={company.phone} />

              <DetailField label="Email">
                {company.email && (
                  <a href={`mailto:${company.email}`} className="text-[#0A0A9E] hover:underline">
                    {company.email}
                  </a>
                )}
              </DetailField>
            </div>
          </DetailCard>

          {/* Adjustors Section - Right Column (wider) */}
          <DetailCard
            title={`Adjustors (${company.adjustorCount})`}
            className="lg:col-span-2"
            titleAction={
              <div className="flex items-center gap-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="showInactiveAdjustors"
                    checked={showInactiveAdjustors}
                    onChange={(e) => setShowInactiveAdjustors(e.target.checked)}
                  />
                  <Label htmlFor="showInactiveAdjustors" className="text-sm font-normal cursor-pointer">
                    Show inactive
                  </Label>
                </div>

                <Button onClick={handleAddAdjustor} size="sm" className="gap-1">
                  <Plus className="h-4 w-4" />
                  Add Adjustor
                </Button>
              </div>
            }
          >
            <AdjustorList
              adjustors={adjustors}
              onEdit={handleEditAdjustor}
              onDeactivate={handleDeactivateAdjustor}
              isLoading={isLoadingAdjustors}
            />
          </DetailCard>
        </div>
      </div>

      {/* Company Form Dialog */}
      <InsuranceCompanyForm
        open={isCompanyFormOpen}
        onOpenChange={setIsCompanyFormOpen}
        company={company}
        onSuccess={handleCompanyFormSuccess}
      />

      {/* Adjustor Form Dialog */}
      {company && (
        <AdjustorForm
          open={isAdjustorFormOpen}
          onOpenChange={setIsAdjustorFormOpen}
          companyId={company.id}
          companyName={company.name}
          adjustor={selectedAdjustor}
          onSuccess={handleAdjustorFormSuccess}
        />
      )}
    </div>
  );
}
